import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Clock, Tag } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import Breadcrumb from "../Breadcrumb";
import FAQSection from "../FAQSection";
import { getPostBySlug, getRelatedPosts } from "../../data/blogPosts";
import { getCategoryName } from "../../data/blogCategories";
import { business } from "../../config/business";

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/resources" replace />;

  const related = getRelatedPosts(post, 3);

  // Long-form article titles are kept as-is for the H1 and on-page display,
  // but the brand suffix is only appended to the <title> tag when it still
  // fits Google's ~60-char SERP display width.
  const seoTitle = post.title.length <= 40 ? `${post.title} | Vasavi Structures` : post.title;
  const seoDescription =
    post.excerpt.length <= 155 ? post.excerpt : `${post.excerpt.slice(0, 155).replace(/\s+\S*$/, "")}…`;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.publishDate,
    dateModified: post.publishDate,
    author: { "@type": "Organization", name: business.name },
    publisher: { "@type": "Organization", name: business.name },
    mainEntityOfPage: `${business.siteUrl}/resources/${post.slug}`,
  };

  return (
    <Layout>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={post.keywords}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>
      <Breadcrumb
        items={[
          { name: "Resources", path: "/resources" },
          { name: getCategoryName(post.category), path: `/resources?category=${post.category}` },
          { name: post.title },
        ]}
      />

      <article className="section-padding">
        <div className="container-custom max-w-3xl">
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
            <span className="flex items-center gap-1">
              <Tag size={14} aria-hidden="true" /> {getCategoryName(post.category)}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={14} aria-hidden="true" /> {post.readTime}
            </span>
          </div>

          <h1 className="font-montserrat text-3xl md:text-4xl font-bold text-foreground mb-6">
            {post.title}
          </h1>

          {/* Quick-answer block: direct, extractable answer for AEO/GEO (answer engines & AI citations) */}
          {post.quickAnswer && (
            <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-5 mb-10">
              <p className="font-montserrat font-semibold text-foreground mb-1">Quick Answer</p>
              <p className="text-muted-foreground">{post.quickAnswer}</p>
            </div>
          )}

          <div className="prose-content space-y-8">
            {post.sections.map((section) => (
              <div key={section.heading}>
                <h2 className="font-montserrat text-xl md:text-2xl font-bold text-foreground mb-3">
                  {section.heading}
                </h2>
                {section.paragraphs?.map((p, i) => (
                  <p key={i} className="text-muted-foreground leading-relaxed mb-3">{p}</p>
                ))}
                {section.list && (
                  <ul className="space-y-2 mt-3">
                    {section.list.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-muted-foreground">
                        <span className="text-secondary mt-1.5 flex-shrink-0" aria-hidden="true">&#8226;</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <div className="bg-secondary rounded-2xl p-8 mt-12 text-center">
            <h2 className="font-montserrat text-xl md:text-2xl font-bold text-secondary-foreground mb-4">
              Have a Project in Mind?
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/enquiry" className="btn-primary inline-block px-8 py-4">
                Talk to Our Engineering Team
              </Link>
              <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
                <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </article>

      {post.faqs && <FAQSection faqs={post.faqs} />}

      {related.length > 0 && (
        <section className="section-padding">
          <div className="container-custom max-w-5xl">
            <h2 className="font-montserrat text-2xl font-bold text-foreground mb-6">Related Reading</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/resources/${r.slug}`}
                  className="p-5 bg-card rounded-xl shadow-md card-hover border border-border"
                >
                  <p className="text-xs text-secondary font-semibold mb-2">{getCategoryName(r.category)}</p>
                  <h3 className="font-montserrat font-bold text-foreground">{r.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default BlogPost;
